<template>
  <content-popup
    class="search-filter-popup"
    :title="'Filters'"
    :visible="visible"
    :reference-element="contentPopupRefElement"
    boundary=".student-selection-listing-from-group-content-subpage"
    primary-button-label="APPLY"
    secondary-button-label="RESET"
    :show-close-button="true"
    @on-confirm="updateSearchFilter"
    @on-cancel="handleReset"
    @on-close="handleClose"
  >
    <template #content>
      <cv-form>
        <dl v-if="filterFlags.formClass" class="field-set type-dropdown">
          <dt class="field-label">{{ t("student_group_dropdown_form_class_label") }}</dt>
          <dd class="field-input">
            <multi-select
              class="form-class-filter"
              :label="t('student_group_dropdown_form_class_label_placeholder')"
              selection-feedback="fixed"
              :options="formClasses"
              :value="formClassSelected"
              @change="handleFormClassChange"
            />
          </dd>
        </dl>
        <dl v-if="filterFlags.levelStream" class="field-set type-dropdown">
          <dt class="field-label">{{ t("student_group_dropdown_level_label") }}</dt>
          <dd class="field-input">
            <multi-select
              class="level-stream-filter"
              :label="t('student_group_dropdown_level_label_placeholder')"
              selection-feedback="fixed"
              :options="levelStreams"
              :value="levelStreamSelected"
              @change="handleLevelStreamChange"
            />
          </dd>
        </dl>
        <dl v-if="filterFlags.school" class="field-set type-dropdown">
          <dt class="field-label">School</dt>
          <dd class="field-input">
            <combo-box
              :key="schools.length"
              class="school-filter"
              label="Select School"
              :auto-filter="true"
              :options="schools"
              :value="schoolSelected"
              @change="handleSchoolChange"
            />
          </dd>
        </dl>
      </cv-form>
    </template>
  </content-popup>
</template>

<script setup lang="ts">
import DOMUtils from "@utils/dom.utils";
import { ref, watch, onMounted, nextTick } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

// Emits
const emit = defineEmits(["change", "close"]);

// Data
const contentPopupRefElement = ref(null);
const formClassSelected = ref([]);
const levelStreamSelected = ref([]);
const schoolSelected = ref("");

// Props
interface Props {
  formClasses: unknown[];
  levelStreams: unknown[];
  schools: unknown[];
  filterFlags: Record<string, unknown>;
  appliedSearchFilters: Record<string, unknown>;
  visible: boolean;
}
const props = defineProps<Props>();

// Methods
const updateSearchFilter = function () {
  emit("change", {
    school: schoolSelected.value,
    formClasses: formClassSelected.value,
    levelStreams: levelStreamSelected.value,
  });
};

const handleReset = function () {
  schoolSelected.value = "";
  formClassSelected.value = [];
  levelStreamSelected.value = [];
};

const handleClose = function () {
  updateFilterSelection();
  emit("close");
};

const updateFilterSelection = function () {
  const filters = props.appliedSearchFilters || {};
  formClassSelected.value = filters.formClasses || [];
  levelStreamSelected.value = filters.levelStreams || [];
  schoolSelected.value = filters.school || "";
};

const handleFormClassChange = function (formClasses) {
  formClassSelected.value = formClasses;
};

const handleLevelStreamChange = function (levelStreams) {
  levelStreamSelected.value = levelStreams;
};

const handleSchoolChange = function (school) {
  schoolSelected.value = school;
};

const getReferenceContainer = function () {
  return DOMUtils.elOf(
    document,
    ".student-selection-listing-from-group-content-subpage .search-bar-wrapper",
  )[0];
};

// Watch
watch(
  () => props.appliedSearchFilters,
  function () {
    updateFilterSelection();
  },
);

// Mounted
onMounted(() => {
  // Created
  updateFilterSelection();
  updateSearchFilter();

  nextTick(() => {
    contentPopupRefElement.value = getReferenceContainer();
  });
});
</script>
